import pick from 'lodash/fp/pick';
import pickBy from 'lodash/pickBy';

import config from '@config';

import createDataSource from '../createDataSource';
import { getDefaultEmptyList, getDefaultListHandler } from '../utils';

const BASE_PATHNAME = 'articles';

export default () => {
  const dataSource = createDataSource(`${config.api.url}`);

  const getById = ({ id, locale }) => {
    if (locale) {
      return dataSource.get(`${BASE_PATHNAME}/${id}/${locale}`).json();
    }
    return dataSource.get(`${BASE_PATHNAME}/${id}`).json();
  };

  const getByIds = ({ ids, locale, passErrors }) =>
    Promise.all(
      ids.map(async (id) => {
        try {
          const result = await getById({ id, locale });
          return result;
        } catch (e) {
          if (passErrors) {
            // hero carousel relies on id for filtering
            e.id = id;
            return e;
          }
          throw e;
        }
      }),
    );

  const remove = ({ id, contentId, language }) => {
    if (contentId) {
      return dataSource.delete(`${BASE_PATHNAME}/${contentId}/${language}`);
    }
    return dataSource.delete(`${BASE_PATHNAME}/${id}/${language}`);
  };

  const create = ({ values }) => {
    return dataSource.post(BASE_PATHNAME, { json: { ...values } }).json();
  };

  const update = ({ id, values }) => {
    return dataSource.put(`${BASE_PATHNAME}`, { json: { ...values, id } }).json();
  };

  const publish = async ({ id, values, dateTime }) => {
    const article = await (id ? update({ id, values }) : create({ values }));

    if (article.language) {
      return dataSource
        .put(`${BASE_PATHNAME}/${article.id}/${article.language}/publish`, { searchParams: pickBy({ dateTime }) })
        .json();
    }

    return dataSource.put(`${BASE_PATHNAME}/${article.id}/publish`, { searchParams: pickBy({ dateTime }) }).json();
  };

  const unpublish = ({ id, contentId, language }) => {
    if (contentId) {
      return dataSource.put(`${BASE_PATHNAME}/${contentId}/${language}/unpublish`).json();
    }
    return dataSource.put(`${BASE_PATHNAME}/${id}/unpublish`).json();
  };

  const duplicate = ({ contentId }) => dataSource.post(`${BASE_PATHNAME}/${contentId}/duplicate`).json();

  return {
    getList: getDefaultListHandler({
      url: BASE_PATHNAME,
      dataSource,
      transformFilter: pick(['type', 'tag', 'eventId', 'liveBlog', 'fromThisWeek', 'language']),
      defaultFilters: { language: 'en' },
    }),

    getPublishedList: getDefaultListHandler({
      url: BASE_PATHNAME,
      dataSource,
      transformFilter: pick(['type', 'tag', 'eventId', 'liveBlog', 'fromThisWeek', 'language']),
      defaultFilters: { language: 'en', isPublished: true },
    }),

    getById,

    getByIds,

    remove,

    create,

    update,

    publish,

    unpublish,

    duplicate,

    getEmptyList: getDefaultEmptyList,
  };
};
